import React, { useState, useEffect } from 'react';
import { StoreOffer, GownType, GownStyle } from '../../api/StoreOffersAPI';
import { Item } from '../../api/CollectionsAPI';
import { ChevronDown, ChevronUp } from 'lucide-react';
import InputShortText from '../InputShortText/InputShortText';
import TextArea from '../TextArea/TextArea';
import SelectInput from '../SelectInput/SelectInput';
import PriceField from '../PriceField/PriceField';
import Switch from '../Switch/Switch';
import FileUploadWrapper from '../FileUploadWrapper/FileUploadWrapper';
import ImageGallery from '../ImageGallery/ImageGallery';
import ColorPicker from '../ColorPicker/ColorPicker';
import InputNumber from '../InputNumber/InputNumber';
import { ItemAutocomplete } from '../ItemAutocomplete/ItemAutocomplete';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import SectionTitle from '../SectionTitle/SectionTitle';
import CONFIG from '../../config/config';
import { isValidText } from '../../shared/validation';
import ImageEditorModal from '../ImageEditorModal/ImageEditorModal';

type Props = {
  offer: StoreOffer;
  onChange: (offer: StoreOffer, errors: Record<string, string>) => void;
  isDisabled?: boolean;
  showOnlySection?: 'basic' | 'pricing' | 'availability' | 'gown' | 'images' | 'details';
  isCreating?: boolean; // True when creating a new offer (not editing)
};

function StoreOfferForm({ offer, onChange, isDisabled = false, showOnlySection, isCreating = false }: Props) {
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [createNewItem, setCreateNewItem] = useState<boolean>(!offer.itemId);
  const [isAdditionalDetailsExpanded, setIsAdditionalDetailsExpanded] = useState<boolean>(false);
  const [isImageEditorOpen, setIsImageEditorOpen] = useState<boolean>(false);
  const [imageToEdit, setImageToEdit] = useState<string>('');
  
  const showSection = (section: 'basic' | 'pricing' | 'availability' | 'gown' | 'images' | 'details'): boolean => {
    if (!showOnlySection) return true;
    return showOnlySection === section;
  };

  const gownTypeOptions = [
    { value: 'ballgown', readableValue: 'store.form.gown_type.ballgown' },
    { value: 'mermaid', readableValue: 'store.form.gown_type.mermaid' },
    { value: 'a-line', readableValue: 'store.form.gown_type.a_line' },
    { value: 'sheath', readableValue: 'store.form.gown_type.sheath' },
    { value: 'trumpet', readableValue: 'store.form.gown_type.trumpet' },
    { value: 'tea-length', readableValue: 'store.form.gown_type.tea_length' },
    { value: 'other', readableValue: 'store.form.gown_type.other' },
  ];

  const gownStyleOptions = [
    { value: 'classic', readableValue: 'store.form.gown_style.classic' },
    { value: 'modern', readableValue: 'store.form.gown_style.modern' },
    { value: 'vintage', readableValue: 'store.form.gown_style.vintage' },
    { value: 'boho', readableValue: 'store.form.gown_style.boho' },
    { value: 'romantic', readableValue: 'store.form.gown_style.romantic' },
    { value: 'minimalist', readableValue: 'store.form.gown_style.minimalist' },
  ];


  const validateForm = (currentOffer: StoreOffer): Record<string, string> => {
    if (!currentOffer) return {};
    
    const errors: Record<string, string> = {};

    if (!currentOffer.name || !isValidText(currentOffer.name, 3)) {
      errors.name = 'Name must be at least 3 characters';
    }

    if (currentOffer.price !== undefined && currentOffer.price < 0) {
      errors.price = 'Price must be 0 or greater';
    }

    if (currentOffer.onSale && currentOffer.salePrice !== undefined) {
      if (currentOffer.salePrice < 0) {
        errors.salePrice = 'Sale price must be 0 or greater';
      }
      if (currentOffer.price !== undefined && currentOffer.salePrice >= currentOffer.price) {
        errors.salePrice = 'Sale price must be lower than regular price';
      }
    }

    if (currentOffer.available && currentOffer.quantity !== undefined) {
      if (currentOffer.quantity < 0) {
        errors.quantity = 'Quantity must be 0 or greater';
      }
    }

    return errors;
  };

  const handleChange = (updates: Partial<StoreOffer>) => {
    try {
      const updatedOffer = { ...offer, ...updates };
      const errors = validateForm(updatedOffer);
      setFormErrors(errors);
      onChange(updatedOffer, errors);
    } catch (error) {
      console.error('Error in handleChange:', error);
      setFormErrors({ general: 'An error occurred while updating the form' });
    }
  };

  const handleImageUpload = (files: File[]) => {
    try {
      // In a real implementation, you would upload files and get URLs
      // For now, we'll use object URLs as placeholders
      const newImageUrls = files.map(file => URL.createObjectURL(file));
      const currentImages = offer?.images || [];
      handleChange({ images: [...currentImages, ...newImageUrls] });
    } catch (error) {
      console.error('Error uploading images:', error);
    }
  };

  const handleRemoveImage = (imageUrl: string) => {
    try {
      const currentImages = offer?.images || [];
      handleChange({ images: currentImages.filter(img => img !== imageUrl) });
    } catch (error) {
      console.error('Error removing image:', error);
    }
  };

  const handleEditImage = (imageUrl: string) => {
    setImageToEdit(imageUrl);
    setIsImageEditorOpen(true);
  };

  const handleImageEditSave = (editedImageUrl: string) => {
    try {
      const currentImages = offer?.images || [];
      const updatedImages = currentImages.map(img => 
        img === imageToEdit ? editedImageUrl : img
      );
      
      // Update featured image if it was the edited one
      const updatedFeaturedImage = offer.featuredImage === imageToEdit 
        ? editedImageUrl 
        : offer.featuredImage;
      
      handleChange({ 
        images: updatedImages,
        featuredImage: updatedFeaturedImage
      });
      
      setIsImageEditorOpen(false);
      setImageToEdit('');
    } catch (error) {
      console.error('Error saving edited image:', error);
    }
  };

  useEffect(() => {
    if (offer) {
      const errors = validateForm(offer);
      setFormErrors(errors);
    }
    // Sync createNewItem state with offer.itemId
    if (isCreating) {
      setCreateNewItem(!offer.itemId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer, isCreating]);

  // Safety check after hooks
  if (!offer) {
    return <div className="p-4 text-gray-500">Loading form...</div>;
  }

  return (
    <div className="space-y-8">
      {/* Basic Information Section */}
      {showSection('basic') && (
      <div className="space-y-4">
        <SectionTitle text="Basic Information" />
        <div className="space-y-4">
          {isCreating ? (
            !createNewItem ? (
              <>
                <ItemAutocomplete
                  label="Search for existing item"
                  placeholder="Type to search for an item..."
                  onChange={(item: Item) => {
                    handleChange({
                      name: item.name || '',
                      description: item.description || '',
                      itemId: item.id,
                      images: item.media || [],
                      featuredImage: item.media?.[0],
                    });
                  }}
                  defaultValue={offer.itemId ? offer.name : ''}
                  isDisabled={isDisabled}
                />
                <div className="flex items-center justify-center py-2">
                  <div className="relative flex items-center">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <span className="flex-shrink mx-4 text-gray-500 text-sm">or</span>
                    <div className="flex-grow border-t border-gray-300"></div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <PrimaryButton
                    text="Create new item from scratch"
                    fn={() => {
                      setCreateNewItem(true);
                      handleChange({ itemId: undefined });
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-600">Creating new item</p>
                  <button
                    type="button"
                    onClick={() => {
                      setCreateNewItem(false);
                      handleChange({ name: '', description: '', itemId: undefined });
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700 underline"
                  >
                    Search for existing item instead
                  </button>
                </div>
                <InputShortText
                  label="Name*"
                  validationFn={(value) => !!value && isValidText(value, 3)}
                  onChange={(e) => handleChange({ name: e.currentTarget.value })}
                  defaultValue={offer.name}
                  isDisabled={isDisabled}
                  errorMessage="errors.too_short"
                />

                <TextArea
                  label="Description"
                  onChange={(e) => handleChange({ description: e.currentTarget.value })}
                  defaultValue={offer.description || ''}
                  isDisabled={isDisabled}
                  maxLength={500}
                />
              </>
            )
          ) : (
            <>
              <InputShortText
                label="Name*"
                validationFn={(value) => !!value && isValidText(value, 3)}
                onChange={(e) => handleChange({ name: e.currentTarget.value })}
                defaultValue={offer.name}
                isDisabled={isDisabled}
                errorMessage="errors.too_short"
              />

              <TextArea
                label="Description"
                onChange={(e) => handleChange({ description: e.currentTarget.value })}
                defaultValue={offer.description || ''}
                isDisabled={isDisabled}
                maxLength={500}
              />
            </>
          )}
        </div>
      </div>
      )}


      {/* Pricing Section */}
      {showSection('pricing') && (
      <div className="space-y-4">
        <SectionTitle text="Pricing" />
        <div className="space-y-4">
          <PriceField
            label="Regular Price"
            currency={offer.currency}
            price={offer.price}
            currencies={[''].concat(CONFIG.SUPPORTED_CURRENCIES)}
            onCurrencyChange={(currency) => handleChange({ currency })}
            onPriceChange={(price) => handleChange({ price })}
            isDisabled={isDisabled}
          />

          <div className="flex items-center space-x-3">
            <Switch
              id="onSale"
              checked={offer.onSale || false}
              onChange={() => handleChange({ onSale: !(offer.onSale || false), salePrice: !(offer.onSale || false) ? (offer.price || 0) * 0.8 : undefined })}
            />
            <label htmlFor="onSale" className="text-sm font-medium">
              On Sale
            </label>
          </div>

          {(offer.onSale || false) && (
            <div className="pl-8 border-l-2 border-primary-200">
              <PriceField
                label="Sale Price"
                currency={offer.currency}
                price={offer.salePrice || 0}
                currencies={[''].concat(CONFIG.SUPPORTED_CURRENCIES)}
                onCurrencyChange={(currency) => handleChange({ currency })}
                onPriceChange={(salePrice) => handleChange({ salePrice })}
                isDisabled={isDisabled}
              />
              {formErrors.salePrice && (
                <p className="text-xs text-red-600 mt-1">{formErrors.salePrice}</p>
              )}
            </div>
          )}
        </div>
      </div>
      )}


      {/* Availability Section */}
      {showSection('availability') && (
      <div className="space-y-4">
        <SectionTitle text="Availability" />
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <Switch
              id="available"
              checked={offer.available !== undefined ? offer.available : true}
              onChange={() => {
                const newAvailable = !(offer.available !== undefined ? offer.available : true);
                handleChange({ available: newAvailable, quantity: newAvailable ? offer.quantity : undefined });
              }}
            />
            <label htmlFor="available" className="text-sm font-medium">
              Available
            </label>
          </div>

          {(offer.available !== undefined ? offer.available : true) && (
            <div className="pl-8 border-l-2 border-primary-200">
              <InputNumber
                label="Quantity Available (optional)"
                validationFn={(value) => value >= 0}
                onChange={(e) => {
                  const quantity = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                  handleChange({ quantity: isNaN(quantity as number) ? undefined : quantity });
                }}
                defaultValue={offer.quantity}
                isDisabled={isDisabled}
                placeholder="Leave empty for unlimited"
                errorMessage="errors.minimum_zero"
              />
              {formErrors.quantity && (
                <p className="text-xs text-red-600 mt-1">{formErrors.quantity}</p>
              )}
            </div>
          )}

          <div className="flex items-center space-x-3">
            <Switch
              id="featured"
              checked={offer.featured || false}
              onChange={() => handleChange({ featured: !(offer.featured || false) })}
            />
            <label htmlFor="featured" className="text-sm font-medium">
              Featured
            </label>
          </div>
        </div>
      </div>
      )}


      {/* Gown Details Section */}
      {showSection('gown') && (
      <div className="space-y-4">
        <SectionTitle text="Gown Details" />
        <div className="space-y-4">
          <SelectInput
            label="Gown Type"
            values={gownTypeOptions}
            defaultValue={offer.gownType}
            onChange={(e) => handleChange({ gownType: e.currentTarget.value as GownType })}
            isDisabled={isDisabled}
          />

          <SelectInput
            label="Gown Style"
            values={gownStyleOptions}
            defaultValue={offer.gownStyle}
            onChange={(e) => handleChange({ gownStyle: e.currentTarget.value as GownStyle })}
            isDisabled={isDisabled}
          />

          <ColorPicker
            label="Gown Color"
            value={offer.gownColor || 'white'}
            onChange={(color) => handleChange({ gownColor: color })}
            isDisabled={isDisabled}
          />

          <InputShortText
            label="Size"
            onChange={(e) => handleChange({ size: e.currentTarget.value })}
            defaultValue={offer.size || ''}
            isDisabled={isDisabled}
          />
        </div>
      </div>
      )}


      {/* Images Section */}
      {showSection('images') && (
      <div className="space-y-4">
        <SectionTitle text="Images" />
        <div>
          {offer?.images && offer.images.length > 0 && (
            <div className="mb-4">
              <ImageGallery
                images={offer.images}
                canRemoveImages={!isDisabled}
                canEditImages={!isDisabled}
                onImageRemove={handleRemoveImage}
                onImageEdit={handleEditImage}
              />
            </div>
          )}
          <FileUploadWrapper
            label="Upload Images"
            onChange={handleImageUpload}
            isDisabled={isDisabled}
            allowedTypes={CONFIG.IMAGE_TYPES}
            allowedMultipleFiles
          />
        </div>
      </div>
      )}


      {/* Additional Details Section */}
      {showSection('details') && (
      <div className="space-y-4">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setIsAdditionalDetailsExpanded(!isAdditionalDetailsExpanded)}
        >
          <SectionTitle text="Additional Details" />
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setIsAdditionalDetailsExpanded(!isAdditionalDetailsExpanded);
            }}
          >
            {isAdditionalDetailsExpanded ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </button>
        </div>
        {isAdditionalDetailsExpanded && (
          <div className="space-y-6">
          {/* Dress Features */}
          <div>
            <p className="text-sm font-extrabold mb-3">Dress Features</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="fitAndFlare"
                  checked={offer.dressFeatures?.fitAndFlare || false}
                  onChange={() => handleChange({
                    dressFeatures: {
                      ...offer.dressFeatures,
                      fitAndFlare: !(offer.dressFeatures?.fitAndFlare || false),
                    }
                  })}
                />
                <label htmlFor="fitAndFlare" className="text-sm font-medium">Fit & Flare</label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="sweetheart"
                  checked={offer.dressFeatures?.sweetheart || false}
                  onChange={() => handleChange({
                    dressFeatures: {
                      ...offer.dressFeatures,
                      sweetheart: !(offer.dressFeatures?.sweetheart || false),
                    }
                  })}
                />
                <label htmlFor="sweetheart" className="text-sm font-medium">Sweetheart</label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="lowBack"
                  checked={offer.dressFeatures?.lowBack || false}
                  onChange={() => handleChange({
                    dressFeatures: {
                      ...offer.dressFeatures,
                      lowBack: !(offer.dressFeatures?.lowBack || false),
                    }
                  })}
                />
                <label htmlFor="lowBack" className="text-sm font-medium">Low Back</label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="strapless"
                  checked={offer.dressFeatures?.strapless || false}
                  onChange={() => handleChange({
                    dressFeatures: {
                      ...offer.dressFeatures,
                      strapless: !(offer.dressFeatures?.strapless || false),
                    }
                  })}
                />
                <label htmlFor="strapless" className="text-sm font-medium">Strapless</label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="floorLength"
                  checked={offer.dressFeatures?.floorLength || false}
                  onChange={() => handleChange({
                    dressFeatures: {
                      ...offer.dressFeatures,
                      floorLength: !(offer.dressFeatures?.floorLength || false),
                    }
                  })}
                />
                <label htmlFor="floorLength" className="text-sm font-medium">Floor Length</label>
              </div>
            </div>
          </div>

          {/* Special Features */}
          <div>
            <p className="text-sm font-extrabold mb-2">Special Features</p>
            <InputShortText
              label="Add special features (comma-separated)"
              onChange={(e) => {
                const features = e.currentTarget.value.split(',').map(f => f.trim()).filter(f => f.length > 0);
                handleChange({ specialFeatures: features });
              }}
              defaultValue={offer.specialFeatures?.join(', ') || ''}
              isDisabled={isDisabled}
              placeholder="e.g., Detachable sleeves, Removable train"
            />
          </div>

          {/* Condition */}
          <div>
            <p className="text-sm font-extrabold mb-3">Condition</p>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Switch
                  id="isNew"
                  checked={offer.condition?.isNew || false}
                  onChange={() => handleChange({
                    condition: {
                      ...offer.condition,
                      isNew: !(offer.condition?.isNew || false),
                    }
                  })}
                />
                <label htmlFor="isNew" className="text-sm font-medium">New (unworn)</label>
              </div>
              {!offer.condition?.isNew && (
                <div className="pl-8 border-l-2 border-primary-200 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="hasFlaws"
                      checked={offer.condition?.hasFlaws || false}
                      onChange={() => handleChange({
                        condition: {
                          ...offer.condition,
                          hasFlaws: !(offer.condition?.hasFlaws || false),
                        }
                      })}
                    />
                    <label htmlFor="hasFlaws" className="text-sm font-medium">Has flaws or wear</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="dryCleaned"
                      checked={offer.condition?.dryCleaned || false}
                      onChange={() => handleChange({
                        condition: {
                          ...offer.condition,
                          dryCleaned: !(offer.condition?.dryCleaned || false),
                        }
                      })}
                    />
                    <label htmlFor="dryCleaned" className="text-sm font-medium">Dry cleaned</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="hasBeenAltered"
                      checked={offer.condition?.hasBeenAltered || false}
                      onChange={() => handleChange({
                        condition: {
                          ...offer.condition,
                          hasBeenAltered: !(offer.condition?.hasBeenAltered || false),
                        }
                      })}
                    />
                    <label htmlFor="hasBeenAltered" className="text-sm font-medium">Has been altered</label>
                  </div>
                  <InputShortText
                    label="Fabric type (optional)"
                    onChange={(e) => handleChange({
                      condition: {
                        ...offer.condition,
                        fabricType: e.currentTarget.value,
                      }
                    })}
                    defaultValue={offer.condition?.fabricType || ''}
                    isDisabled={isDisabled}
                    placeholder="e.g., Lace, Satin, Tulle"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Returns Policy */}
          <div>
            <p className="text-sm font-extrabold mb-3">Returns Policy</p>
            <p className="text-xs text-gray-500 mb-3">Select which return reasons are accepted</p>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Switch
                  id="returnMisrepresented"
                  checked={offer.returnsPolicy?.misrepresented !== false}
                  onChange={() => handleChange({
                    returnsPolicy: {
                      ...offer.returnsPolicy,
                      misrepresented: !(offer.returnsPolicy?.misrepresented !== false),
                    }
                  })}
                />
                <label htmlFor="returnMisrepresented" className="text-sm font-medium">Misrepresented</label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="returnCounterfeit"
                  checked={offer.returnsPolicy?.counterfeit !== false}
                  onChange={() => handleChange({
                    returnsPolicy: {
                      ...offer.returnsPolicy,
                      counterfeit: !(offer.returnsPolicy?.counterfeit !== false),
                    }
                  })}
                />
                <label htmlFor="returnCounterfeit" className="text-sm font-medium">Counterfeit</label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="returnDoesntFit"
                  checked={offer.returnsPolicy?.doesntFit || false}
                  onChange={() => handleChange({
                    returnsPolicy: {
                      ...offer.returnsPolicy,
                      doesntFit: !(offer.returnsPolicy?.doesntFit || false),
                    }
                  })}
                />
                <label htmlFor="returnDoesntFit" className="text-sm font-medium">Doesn't fit</label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="returnDamaged"
                  checked={offer.returnsPolicy?.damaged !== false}
                  onChange={() => handleChange({
                    returnsPolicy: {
                      ...offer.returnsPolicy,
                      damaged: !(offer.returnsPolicy?.damaged !== false),
                    }
                  })}
                />
                <label htmlFor="returnDamaged" className="text-sm font-medium">Damaged</label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="returnNeverArrives"
                  checked={offer.returnsPolicy?.neverArrives !== false}
                  onChange={() => handleChange({
                    returnsPolicy: {
                      ...offer.returnsPolicy,
                      neverArrives: !(offer.returnsPolicy?.neverArrives !== false),
                    }
                  })}
                />
                <label htmlFor="returnNeverArrives" className="text-sm font-medium">Never arrives</label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="returnChangeOfMind"
                  checked={offer.returnsPolicy?.changeOfMind || false}
                  onChange={() => handleChange({
                    returnsPolicy: {
                      ...offer.returnsPolicy,
                      changeOfMind: !(offer.returnsPolicy?.changeOfMind || false),
                    }
                  })}
                />
                <label htmlFor="returnChangeOfMind" className="text-sm font-medium">Change of mind</label>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputShortText
              label="Label Size"
              onChange={(e) => handleChange({ labelSize: e.currentTarget.value })}
              defaultValue={offer.labelSize || ''}
              isDisabled={isDisabled}
              placeholder="e.g., EU 38"
            />
            <InputNumber
              label="Year"
              validationFn={(value) => value >= 1900 && value <= new Date().getFullYear() + 1}
              onChange={(e) => handleChange({ year: e.target.value ? parseInt(e.target.value, 10) : undefined })}
              defaultValue={offer.year}
              isDisabled={isDisabled}
              placeholder="e.g., 2023"
            />
          </div>
        </div>
        )}
      </div>
      )}
      
      {/* Image Editor Modal */}
      <ImageEditorModal
        isOpen={isImageEditorOpen}
        onClose={() => {
          setIsImageEditorOpen(false);
          setImageToEdit('');
        }}
        initialImageUrl={imageToEdit}
        onSave={handleImageEditSave}
      />
    </div>
  );
}

export default StoreOfferForm;

